import React, { useContext, useMemo } from 'react';
import PropTypes from 'prop-types';

import { Badge, Icon } from '@openedx/paragon';
import { CheckCircle, PushPin, SubdirectoryArrowRight } from '@openedx/paragon/icons';
import classNames from 'classnames';
import { useSelector } from 'react-redux';
import { Link, useLocation } from 'react-router-dom';

import { useIntl } from '@edx/frontend-platform/i18n';

import { AvatarOutlineAndLabelColors, Routes, ThreadType } from '../../../data/constants';
import AuthorLabel from '../../common/AuthorLabel';
import DiscussionContext from '../../common/context';
import { discussionsPath, getAuthorRoles, isPostPreviewAvailable } from '../../utils';
import { selectThread } from '../data/selectors';
import messages from './messages';
import { PostAvatar } from './PostHeader';
import PostSummaryFooter from './PostSummaryFooter';

const PostLink = ({
  idx,
  postId,
  showDivider,
}) => {
  const intl = useIntl();
  const { search } = useLocation();
  const {
    courseId,
    postId: selectedPostId,
    page,
    enableInContextSidebar,
    category,
    learnerUsername,
  } = useContext(DiscussionContext);
  const threadData = useSelector(selectThread(postId));
  const {
    topicId,
    hasEndorsed,
    type,
    author,
    authorLabel: rawAuthorLabel,
    authorLabels,
    abuseFlagged,
    abuseFlaggedCount,
    read,
    commentCount,
    unreadCommentCount,
    id,
    pinned,
    previewBody,
    title,
    voted,
    voteCount,
    following,
    groupId,
    groupName,
    createdAt,
    users: postUsers,
    isDeleted,
    threadTitle,
    commentThreadId,
  } = threadData;
  const authorLabel = (authorLabels && authorLabels.length > 0) ? authorLabels : rawAuthorLabel;

  // Get the type label to display
  const getTypeLabel = () => {
    if (type === 'response') {
      return 'Response';
    }
    if (type === 'comment') {
      return 'Comment';
    }
    return null;
  };

  // For comments/responses, show parent thread title with arrow
  const displayTitle = (type === 'response' || type === 'comment') && threadTitle ? threadTitle : title;

  // Strip render_id suffix (e.g., "-thread", "-response", "-comment") for navigation
  const stripRenderIdSuffix = (idValue) => {
    if (typeof idValue === 'string') {
      return idValue.replace(/-(thread|response|comment)$/, '');
    }
    return idValue;
  };

  // For comments/responses, navigate to the parent thread instead of the comment itself
  const rawNavigationId = (type === 'response' || type === 'comment') && commentThreadId ? commentThreadId : postId;
  const navigationPostId = stripRenderIdSuffix(rawNavigationId);

  const { pathname } = discussionsPath(Routes.COMMENTS.PAGES[page], {
    0: enableInContextSidebar ? 'in-context' : undefined,
    courseId,
    topicId,
    postId: navigationPostId,
    category,
    learnerUsername,
  })();
  const showAnsweredBadge = hasEndorsed && type === ThreadType.QUESTION;
  const firstRole = getAuthorRoles(authorLabel)[0];
  const authorLabelColor = AvatarOutlineAndLabelColors[firstRole];
  const canSeeReportedBadge = !!(abuseFlagged || abuseFlaggedCount);
  const isPostRead = read || (!read && commentCount !== unreadCommentCount);
  const shouldUseSingleLineAuthorRole = ['learners', 'posts', 'my-posts'].includes(page);

  // Check if viewing a muted user's activity and if this post is from non-muted author
  const personalMutedUsers = useSelector(state => state.learners?.mutedUsers?.personal || []);
  const courseWideMutedUsers = useSelector(state => state.learners?.mutedUsers?.course || []);
  const allMutedUsers = [...personalMutedUsers, ...courseWideMutedUsers];

  // If learnerUsername exists, we're viewing a specific user's activity
  // Show icon if: learnerUsername is muted AND post author is NOT the muted user (so post contains muted comments)
  const isViewingMutedUser = learnerUsername && allMutedUsers.includes(learnerUsername);
  const isPostAuthorNotMuted = author && author !== learnerUsername;
  const showMutedContentIcon = isViewingMutedUser && isPostAuthorNotMuted;

  const checkIsSelected = useMemo(
    () => (
      window.location.pathname.includes(postId)),
    [window.location.pathname],
  );

  return (
    <Link
      className={
        classNames('discussion-post p-0 text-decoration-none text-gray-900', {
          'border-bottom border-light-400': showDivider,
        })
      }
      to={`${pathname}${enableInContextSidebar ? search : ''}`}
      aria-current={checkIsSelected ? 'page' : undefined}
      role="option"
      tabIndex={(checkIsSelected || idx === 0) ? 0 : -1}
    >
      <div
        className={
          classNames(
            'd-flex flex-row pt-2 pb-2 px-4 border-primary-500 position-relative',
            { 'bg-light-300': isPostRead },
            { 'post-summary-card-selected': id === selectedPostId },
            { 'bg-light-200': isDeleted }, // Gray background for deleted threads
          )
        }
        style={isDeleted ? { opacity: 0.7 } : {}} // Slightly faded for deleted threads
      >
        <PostAvatar
          postType={type}
          author={author}
          authorLabel={authorLabel}
          fromPostLink
          read={isPostRead}
          postUsers={postUsers}
        />
        <div className="d-flex flex-column flex-fill" style={{ minWidth: 0 }}>
          <div className="d-flex flex-column justify-content-start mw-100 flex-fill" style={{ marginBottom: '-3px' }}>
            <div className="d-flex align-items-center pb-0 mb-0 flex-fill">
              <div className="text-truncate mr-1">
                {(type === 'response' || type === 'comment') && threadTitle && (
                  <>
                    <Icon
                      src={SubdirectoryArrowRight}
                      className="text-gray-500 align-bottom subdirectory-arrow-icon"
                    />
                    <span className="text-gray-700 font-weight-normal font-style align-bottom mr-1">
                      {getTypeLabel()} in
                    </span>
                  </>
                )}
                <span className={classNames(
                  'font-weight-500 text-primary-500 font-style align-bottom mr-1',
                  { 'font-weight-bolder': !read },
                  { 'text-decoration-line-through': isDeleted }, // Line-through for deleted threads
                )}
                >
                  {showMutedContentIcon && (
                    <Icon
                      src={SubdirectoryArrowRight}
                      className="text-gray-700 align-bottom subdirectory-arrow-icon"
                    />
                  )}
                  {displayTitle}
                </span>
                <span className="text-gray-700 font-weight-normal  font-style align-bottom">
                  {isPostPreviewAvailable(previewBody) ? previewBody : intl.formatMessage(messages.postWithoutPreview)}
                </span>
              </div>
              {showAnsweredBadge && (
                <Icon
                  data-testid="check-icon"
                  src={CheckCircle}
                  className="text-success font-weight-500 ml-auto badge-padding"
                >
                  <span className="sr-only">{' '}answered</span>
                </Icon>
              )}
              {canSeeReportedBadge && (
                <Badge
                  variant="danger"
                  data-testid="reported-post"
                  className={`font-weight-500 badge-padding ${showAnsweredBadge ? 'ml-2' : 'ml-auto'}`}
                >
                  {intl.formatMessage(messages.contentReported)}
                  <span className="sr-only">{' '}reported</span>
                </Badge>
              )}
              {isDeleted && (
                <Badge
                  variant="light"
                  data-testid="deleted-post"
                  className={classNames('font-weight-500 badge-padding bg-light-400 text-dark', {
                    'ml-2': canSeeReportedBadge || showAnsweredBadge,
                    'ml-auto': !canSeeReportedBadge && !showAnsweredBadge,
                  })}
                >
                  {intl.formatMessage(messages.deletedPost)}
                  <span className="sr-only">{' '}deleted</span>
                </Badge>
              )}
              {pinned && (
                <Icon
                  src={PushPin}
                  className={classNames('post-summary-icons-dimensions text-gray-700', {
                    'ml-2': canSeeReportedBadge || showAnsweredBadge || isDeleted,
                    'ml-auto': !canSeeReportedBadge && !showAnsweredBadge && !isDeleted,
                  })}
                />
              )}
            </div>
          </div>
          <AuthorLabel
            author={author || intl.formatMessage(messages.anonymous)}
            authorLabel={authorLabel}
            labelColor={authorLabelColor && `text-${authorLabelColor}`}
            singleLine={shouldUseSingleLineAuthorRole}
            postData={threadData}
          />
          <PostSummaryFooter
            postId={id}
            voted={voted}
            voteCount={voteCount}
            following={following}
            commentCount={commentCount}
            unreadCommentCount={unreadCommentCount}
            groupId={groupId}
            groupName={groupName}
            createdAt={createdAt}
            preview
            showNewCountLabel={isPostRead}
          />
        </div>
      </div>
      {!showDivider && pinned && <div className="pt-1 bg-light-500 border-top border-light-700" />}
    </Link>
  );
};

PostLink.propTypes = {
  idx: PropTypes.number,
  postId: PropTypes.string.isRequired,
  showDivider: PropTypes.bool,
};

PostLink.defaultProps = {
  idx: -1,
  showDivider: true,
};

export default React.memo(PostLink);
